import { IonIcon } from '@ionic/react'
import { SmartChargingPlan } from '@vae/server/src/interfaces/eNode/eNodeSmartCharging.interface'
import dayjs from 'dayjs'
import { checkmark, flash, pause } from 'ionicons/icons'
import React, { useMemo, useState } from 'react'

import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
dayjs.extend(timezone)

interface SmartChargingGraphProps {
	chargingPlan: SmartChargingPlan
	targetTime: number
}

const SmartChargingGraph: React.FC<SmartChargingGraphProps> = ({ chargingPlan, targetTime }) => {
	const [chargeDuration, setChargeDuration] = useState(0)

	const chargeArr = useMemo(() => {
		const chargeStartTime = dayjs(chargingPlan.startAt)
		const chargeEndTime = dayjs(chargingPlan.estimatedFinishAt)
		const chargeTargetTime = targetTime
		const chargeDuration = Math.abs(
			dayjs(chargingPlan.startAt)
				.startOf('hour')
				.diff(dayjs(chargingPlan.estimatedFinishAt).startOf('hour'), 'hour')
		)
		const chargeHalfPoint = dayjs(chargingPlan.startAt)
			.add(Math.floor(chargeDuration / 2), 'hour')
			.hour()

		const chargePause = dayjs(chargingPlan.stopConfirmedAt).hour()
		setChargeDuration(chargeDuration)

		const arr = []

		for (
			let runningHour = dayjs();
			runningHour.hour() !== chargeTargetTime + 1;
			runningHour = dayjs(runningHour).add(1, 'hour')
		) {
			const startTime = runningHour.hour() === chargeStartTime.hour()
			const endTime = runningHour.hour() === chargeEndTime.hour()
			const targetTime = runningHour.hour() === chargeTargetTime
			const pauseTime = runningHour.hour() === chargePause
			const lastChargingHour = runningHour.hour() === chargeEndTime.hour() - 1
			const halfPoint = runningHour.hour() === chargeHalfPoint

			const charging =
				runningHour.isBefore(chargeEndTime) && runningHour.isAfter(chargeStartTime.minute(0))

			arr.push({
				hour: runningHour.hour(),
				charging,
				startTime,
				endTime,
				targetTime,
				pauseTime,
				lastChargingHour,
				halfPoint,
			})
		}

		const skipAmount = Math.round(arr.length / 4)
		let nextToShow = 0

		return arr.map((element, index) => {
			const showNumber = index === nextToShow || index + 1 === arr.length
			if (showNumber) nextToShow = nextToShow + skipAmount

			return {
				...element,
				showNumber,
			}
		})
	}, [])

	return (
		<div className='smart-charging-visualizer bg-brand-secondary-200-light dark:bg-brand-secondary-100-dark'>
			<div
				className='grid'
				style={{ display: 'grid', gridTemplateColumns: chargeArr.map(() => '1fr').join(' ') }}
			>
				{chargeArr.map(element => (
					<div
						key={element.hour}
						className={`hour icon -translate-x-1/2 ${
							element.pauseTime &&
							'flex items-center justify-center max-w-[1.8rem] rounded-md p-[3px] bg-text-grey-100 dark:bg-[#c3c3c3] text-text-dark'
						} ${
							element.targetTime &&
							'flex items-center justify-center max-w-[1.8rem] rounded-md p-[3px] bg-brand-primary-100-light dark:bg-brand-primary-100-dark text-white'
						}`}
					>
						{element.endTime && <IonIcon icon={checkmark} />}
						{element.pauseTime && <IonIcon icon={pause} />}
					</div>
				))}
				{chargeArr.map(element => (
					<div
						key={element.hour}
						className={`hour chart text-brand-secondary-100-light dark:text-brand-primary-100-dark h-10 flex items-center justify-start ${
							element.charging && 'bg-brand-primary-100-light dark:bg-brand-primary-200-dark'
						} ${element.startTime && 'rounded-l-[0.7rem]'} ${element.endTime && 'end-time'} ${
							element.lastChargingHour &&
							'rounded-r-[0.7rem] bg-opacity-50 bg-brand-primary-100-light dark:bg-brand-primary-200-dark'
						} ${element.targetTime && 'border-l-2 border-l-brand-primary-100-light dark:border-l-brand-primary-100-dark'} ${
							element.pauseTime && 'border-l-2 border-l-text-grey-100 dark:border-l-[#c3c3c3]'
						}`}
					>
						{element.halfPoint && (
							<IonIcon
								style={{ transform: chargeDuration % 2 === 0 && ' translate(-50%)' }}
								icon={flash}
							/>
						)}
					</div>
				))}
				{chargeArr.map(element => (
					<div key={element.hour} className='hour number text-text-grey-300'>
						{element.showNumber && element.hour}
					</div>
				))}
			</div>
		</div>
	)
}
export default SmartChargingGraph
